import { Button as KobalteButton } from "@kobalte/core/button";
import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import { cva, type VariantProps } from "class-variance-authority";
import { splitProps, type ValidComponent } from "solid-js";
import { cn } from "@/app/lib/utils/cn";
import { Tone } from "@/app/components/ui/tone";

export const buttonVariants = cva(
  "inline-flex cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-sans text-base font-normal outline-none ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/85 hover:shadow-md",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md",
        outline:
          "border border-input bg-background hover:border-primary/40 hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline active:scale-100",
        destructive:
          "border border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10",
        warning:
          "border border-warning/30 bg-warning/5 text-warning hover:bg-warning/10",
        success:
          "border border-success/30 bg-success/5 text-success hover:bg-success/10",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-12 rounded-md px-8",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/* The button that raises a given tone wears that tone's colours. */
export const toneButtonVariant: Record<
  Tone,
  VariantProps<typeof buttonVariants>["variant"]
> = {
  [Tone.Default]: "outline",
  [Tone.Success]: "success",
  [Tone.Warning]: "warning",
  [Tone.Destructive]: "destructive",
};

type ButtonProps<T extends ValidComponent = "button"> = PolymorphicProps<T> &
  VariantProps<typeof buttonVariants>;

export const Button = <T extends ValidComponent = "button">(
  props: ButtonProps<T>,
) => {
  const [local, rest] = splitProps(props as ButtonProps, [
    "class",
    "variant",
    "size",
  ]);

  return (
    <KobalteButton
      class={cn(
        buttonVariants({ variant: local.variant, size: local.size }),
        local.class,
      )}
      {...rest}
    />
  );
};
